import { useEffect, useState } from 'react';
import { Download, Smartphone, Apple, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { toast } from 'sonner';

function isStandaloneMode() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isNativeApp() {
    if (typeof window === 'undefined') return false;
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

function wasInstalledBefore() {
    try { return localStorage.getItem('gd_pwa_installed') === '1'; } catch { return false; }
}

function markInstalled() {
    try { localStorage.setItem('gd_pwa_installed', '1'); } catch { /* ignore */ }
}

function isIOS() {
    if (typeof window === 'undefined') return false;
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

function isAndroid() {
    if (typeof window === 'undefined') return false;
    return /android/i.test(window.navigator.userAgent);
}

export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=fun.growdaily.app';

/**
 * PWA Install button.
 * - On Android Chrome/Edge/Samsung: uses beforeinstallprompt event for native install
 * - On iOS Safari: opens /installer with step-by-step guide
 * - Hidden entirely if app is already installed (standalone)
 *
 * Variants: 'card' (big rounded with description), 'inline' (smaller pill-button)
 */
export default function InstallPWAButton({ variant = 'card' }) {
    const navigate = useNavigate();
    const { t } = useI18n();
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(() => wasInstalledBefore());
    const standalone = isStandaloneMode();
    const ios = isIOS();

    useEffect(() => {
        // Already running inside the native app → mark + nothing to install
        if (isNativeApp() || isStandaloneMode()) {
            markInstalled();
            setInstalled(true);
            return;
        }
        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => {
            markInstalled();
            setInstalled(true);
            setDeferred(null);
        };
        window.addEventListener('beforeinstallprompt', onPrompt);
        window.addEventListener('appinstalled', onInstalled);
        // Detect an already-installed PWA/TWA even when viewing in a browser tab
        if (navigator.getInstalledRelatedApps) {
            navigator.getInstalledRelatedApps()
                .then((apps) => { if (apps && apps.length > 0) { markInstalled(); setInstalled(true); } })
                .catch(() => { /* not supported / ignore */ });
        }
        return () => {
            window.removeEventListener('beforeinstallprompt', onPrompt);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    if (standalone || installed || isNativeApp()) return null;

    const android = isAndroid();

    const handleClick = async () => {
        // Android → the official app on Google Play
        if (android) {
            window.open(PLAY_STORE_URL, '_blank', 'noopener');
            return;
        }
        if (deferred) {
            try {
                deferred.prompt();
                const result = await deferred.userChoice;
                if (result.outcome === 'accepted') {
                    markInstalled();
                    setInstalled(true);
                    toast.success(t('install.installing'));
                }
                setDeferred(null);
            } catch (e) {
                console.error('[install] prompt failed', e);
                navigate('/installer');
            }
        } else {
            // No native prompt — open guide
            navigate('/installer');
        }
    };

    const canInstallNative = !!deferred;
    const labelMain = android ? t('install.label_play') : (canInstallNative ? t('install.label_install') : (ios ? t('install.label_ios') : t('install.label_howto')));
    const Icon = ios ? Apple : Smartphone;

    if (variant === 'inline') {
        return (
            <button
                data-testid="btn-install-pwa-inline"
                onClick={handleClick}
                className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white px-4 py-2 rounded-full text-sm transition-colors"
            >
                <Download size={14} strokeWidth={1.8} /> {labelMain}
            </button>
        );
    }

    return (
        <section className="mb-8" data-testid="install-pwa-card">
            <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                <Download size={11} /> {t('install.header')}
            </h2>
            <button
                data-testid="btn-install-pwa"
                onClick={handleClick}
                className="w-full bg-gradient-to-br from-brand-green/15 to-brand-sage/15 border border-brand-green/30 rounded-3xl p-5 text-left hover:shadow-md transition-all hover:-translate-y-0.5"
            >
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-brand-green text-white flex items-center justify-center flex-shrink-0">
                        <Icon size={20} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-serif text-base text-brand-ink leading-snug">
                            {android || canInstallNative ? t('install.title_native') : t('install.title_default')}
                        </div>
                        <div className="text-xs text-brand-ink3 mt-1">
                            {android
                                ? t('install.sub_play')
                                : canInstallNative
                                    ? t('install.sub_native')
                                    : ios
                                        ? t('install.sub_ios')
                                        : t('install.sub_default')}
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-green">
                            {labelMain} <ArrowRight size={12} />
                        </div>
                    </div>
                </div>
            </button>
        </section>
    );
}
